import { quintOut, backOut, sineOut } from 'svelte/easing';

export const createFadeUp =
	(y = 40, duration = 400) =>
	({ delay = 0, easing = quintOut } = {}) =>
	(_node: HTMLElement) => ({
		delay,
		duration,
		easing,
		css: (t: number) => {
			const transform = `translateY(${(1 - t) * y}px)`;
			const opacity = t.toString();
			return `transform: ${transform}; opacity: ${opacity};`;
		}
	});

export const createStagger =
	(baseDelay = 200, increment = 150) =>
	(index: number) =>
		baseDelay + index * increment;

export const navbarSlide = (_node: HTMLElement) => ({
	delay: 0,
	duration: 500,
	easing: quintOut,
	css: (t: number) => {
		const y = (1 - t) * -50;
		return `transform: translateY(${y}px); opacity: ${t};`;
	}
});

export const fadeUpVariant =
	(delay = 200, y = 40) =>
	(_node: HTMLElement) => ({
		delay,
		duration: 400,
		easing: quintOut,
		css: (t: number) => {
			const transform = `translateY(${(1 - t) * y}px)`;
			const opacity = t.toString();
			return `transform: ${transform}; opacity: ${opacity};`;
		}
	});

export const heroImageVariant = (_node: HTMLElement) => ({
	delay: 500,
	duration: 600,
	easing: quintOut,
	css: (t: number) => {
		const transform = `translateY(${(1 - t) * 50}px)`;
		const opacity = t.toString();
		return `transform: ${transform}; opacity: ${opacity};`;
	}
});

export const gradientFadeScale =
	(delay = 200) =>
	(_node: HTMLElement) => ({
		delay,
		duration: 600,
		easing: quintOut,
		css: (t: number) => {
			const scale = 0.8 + 0.2 * t;
			const opacity = t.toString();
			return `transform: scale(${scale}); opacity: ${opacity};`;
		}
	});

export const lightFromCorner =
	(side: 'left' | 'right' = 'left', delay = 200) =>
	(_node: HTMLElement) => ({
		delay,
		duration: 300,
		easing: quintOut,
		css: (t: number) => {
			const x = side === 'left' ? (1 - t) * -100 : (1 - t) * 100;
			const y = (1 - t) * -100;
			const opacity = t.toString();
			return `transform: translate(${x}px, ${y}px); opacity: ${opacity};`;
		}
	});

// Helper function to create custom card entrance animations similar to Framer Motion's cardVariant
export const createCardEntrance =
	(side: 'left' | 'right', rotateInitial = 10, rotateFinal = -10, index = 0) =>
	(_node: HTMLElement) => ({
		delay: index * 80 + 50,
		duration: 600,
		easing: backOut,
		css: (t: number) => {
			const x = (1 - t) * (side === 'left' ? -120 : 120);
			const rotate = rotateInitial + (rotateFinal - rotateInitial) * t;
			const scale = 0.92 + 0.08 * t;
			const opacity = t.toString();

			return `
			transform: translateX(${x}px) rotate(${rotate}deg) scale(${scale});
			opacity: ${opacity};
		`;
		}
	});

// Spring configuration constants (matching React values)
export const springConfig = {
	stiffness: 0.1, // Svelte uses inverse values compared to Framer Motion
	damping: 0.25
};

// Easing function equivalents
export const easingEquivalents = {
	easeOut: quintOut,
	easeIn: sineOut,
	easeInOut: quintOut,
	linear: (t: number) => t
} as const;

// Animation timing guidelines
export const ANIMATION_TIMINGS = {
	micro: 200, // Micro interactions
	content: 400, // Content elements
	large: 600, // Large elements
	page: 800, // Page transitions
	hover: 250, // Hover effects
	tap: 150, // Button press
	stateChange: 300 // State changes
} as const;

export const STAGGER_DELAYS = {
	elements: 80, // Between elements
	groups: 200, // Between groups
	initial: 100 // Initial delay
} as const;
